import React, { useEffect, useState } from 'react';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import api from '../../utils/api';

const EditCategoryDrawer = ({ isOpen = false, onClose, category, onUpdated }) => {
  const [form, setForm] = useState({ name: '', description: '' });
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (category) {
      setForm({
        name: category.name || '',
        description: category.description || '',
      });
    }
  }, [category]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdate = async () => {
    if (!form.name) {
      setError('El nombre es requerido');
      return;
    }

    setError('');
    setLoading(true);

    try {
      await api.put(`/api/categories/${category.id}`, form);
      onUpdated?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al actualizar');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/api/categories/${category.id}`);
      onUpdated?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al eliminar');
      console.error(err);
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-y-0 right-0 max-w-full flex sm:w-full sm:max-w-[800px]">
        <DialogPanel className="w-full bg-white shadow-2xl rounded-l-lg flex flex-col h-screen sm:rounded-r-lg">
          <div className="p-6 overflow-y-auto flex-1 space-y-6">
            <div>
              <DialogTitle className="text-2xl font-bold text-gray-900">Editar Categoría</DialogTitle>
              <p className="mt-2 text-sm text-gray-600">Actualiza los detalles de la categoría. El campo nombre es obligatorio.</p>
            </div>
            <div className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">Nombre *</label>
                <p className="text-xs text-gray-500 mt-1">El nombre de la categoría (e.g., "Aplicaciones Móviles").</p>
                <input
                  id="name"
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Nombre de la categoría"
                  className="mt-1 block w-full border border-gray-200 rounded-md shadow-sm p-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">Descripción</label>
                <p className="text-xs text-gray-500 mt-1">Breve descripción de la categoría (opcional).</p>
                <textarea
                  id="description"
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  placeholder="Descripción de la categoría"
                  className="mt-1 block w-full border border-gray-200 rounded-md shadow-sm p-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  rows="4"
                />
              </div>
              {error && <p className="text-red-500 text-sm">{error}</p>}
            </div>
            <div className="mt-10">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Eliminar Categoría</h3>
              <p className="text-sm text-gray-600 mb-4">Elimina permanentemente esta categoría del sistema. Esta acción no se puede deshacer.</p>
              <div className="flex items-center gap-4 border border-red-200 bg-red-50 p-4 rounded">
                <ExclamationTriangleIcon className="w-5 h-5 text-red-500" aria-hidden="true" />
                <div className="flex-1">
                  <p className="text-sm text-red-600">Confirmar la eliminación de la categoría "{form.name}".</p>
                </div>
                {confirmDelete ? (
                  <button
                    onClick={handleDelete}
                    disabled={deleting}
                    className="text-sm text-white bg-red-600 hover:bg-red-700 px-3 py-1 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    {deleting ? 'Eliminando...' : 'Confirmar Eliminación'}
                  </button>
                ) : (
                  <button
                    onClick={() => setConfirmDelete(true)}
                    className="text-sm text-red-600 hover:underline focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    Eliminar Categoría
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="bg-gray-50 border-t px-6 py-4 flex justify-between gap-4 sticky bottom-0">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading || deleting}
            >
              Cancelar
            </button>
            <button
              onClick={handleUpdate}
              disabled={loading || deleting}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {loading ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

export default EditCategoryDrawer;